import { SaudiRiyal } from "../../components/SaudiRiyal";
import { useEffect, useState } from "react";
import { Plus, Loader2, Play, Building } from "lucide-react";
import { toast } from "sonner";
import { finApi, money, apiErr } from "../api";
import { useFin, PageHeader, Card } from "../FinanceApp";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);

export default function FinAssets() {
  const { can } = useFin();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const load = async () => { setLoading(true); try { const [a, ac, c] = await Promise.all([finApi.assets(), finApi.accounts(false), finApi.assetCategories()]); setRows(a); setAccounts(ac.filter((x) => x.allow_posting)); setCats(c); } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const runDep = async () => {
    setRunning(true);
    try { const r = await finApi.runDepreciation({ as_of_date: today() }); toast.success(`تم ترحيل إهلاك ${r.count} أصل بإجمالي ${money(r.total)} ⃁`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setRunning(false); }
  };
  return (
    <div data-testid="fin-assets">
      <PageHeader title="الأصول الثابتة" subtitle="سجل الأصول والإهلاك الدوري التلقائي"
        action={can("accounts.create") && <div className="flex gap-2">
          <Button variant="outline" onClick={runDep} disabled={running} data-testid="fin-run-depreciation" className="gap-1.5">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} احتساب الإهلاك</Button>
          <Button onClick={() => setOpen(true)} data-testid="fin-add-asset" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"><Plus className="h-4 w-4" /> أصل جديد</Button>
        </div>} />
      <Card className="overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div>
          : rows.length === 0 ? <div className="py-16 text-center text-slate-400">لا توجد أصول</div> : (
            <div className="overflow-x-auto"><table className="w-full text-sm text-right">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">الرمز</th><th className="px-3 py-2 font-semibold">الأصل</th><th className="px-3 py-2 font-semibold">التكلفة</th><th className="px-3 py-2 font-semibold">مجمع الإهلاك</th><th className="px-3 py-2 font-semibold">القيمة الدفترية</th><th className="px-3 py-2 font-semibold">العمر (شهر)</th></tr></thead>
              <tbody className="font-mono-nums">{rows.map((a) => (
                <tr key={a.id} data-testid="fin-asset-row" className="border-t border-slate-50">
                  <td className="px-3 py-2 text-slate-500">{a.code}</td><td className="px-3 py-2 font-sans font-semibold text-slate-800">{a.name}</td>
                  <td className="px-3 py-2" dir="ltr">{money(a.cost)} <SaudiRiyal /></td><td className="px-3 py-2 text-rose-600" dir="ltr">{money(a.accumulated_depreciation)} <SaudiRiyal /></td>
                  <td className="px-3 py-2 font-semibold" dir="ltr">{money(a.book_value)} <SaudiRiyal /></td><td className="px-3 py-2" dir="ltr">{a.months_depreciated}/{a.useful_life_months}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
      </Card>
      <AssetDialog open={open} onOpenChange={setOpen} accounts={accounts} cats={cats} onSaved={load} />
    </div>
  );
}

function AssetDialog({ open, onOpenChange, accounts, cats, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const acc = (code) => accounts.find((a) => a.code === code)?.id || "";
      setF({ name: "", category: cats[0] || "أخرى", purchase_date: today(), cost: "", salvage_value: 0, useful_life_months: 60,
        asset_account_id: acc("1201"), accum_account_id: acc("1202"), expense_account_id: acc("6101") });
    }
  }, [open, accounts, cats]);
  const save = async () => {
    if (!f.name?.trim() || !(parseFloat(f.cost) > 0)) { toast.warning("الاسم والتكلفة مطلوبان"); return; }
    setBusy(true);
    try { await finApi.createAsset({ ...f, cost: parseFloat(f.cost), salvage_value: parseFloat(f.salvage_value) || 0, useful_life_months: parseInt(f.useful_life_months) || 60 }); toast.success("تم حفظ الأصل"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const AccSel = ({ k, label }) => (
    <div className="space-y-1.5"><Label>{label}</Label><Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}><SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger><SelectContent className="max-h-60">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent></Select></div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader className="text-start"><DialogTitle>أصل ثابت جديد</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-1">
          <div className="space-y-1.5 col-span-2"><Label>اسم الأصل</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fin-asset-name" /></div>
          <div className="space-y-1.5"><Label>التصنيف</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>تاريخ الشراء</Label><Input type="date" value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>التكلفة</Label><Input dir="ltr" type="number" value={f.cost || ""} onChange={(e) => setF({ ...f, cost: e.target.value })} data-testid="fin-asset-cost" /></div>
          <div className="space-y-1.5"><Label>القيمة المتبقية</Label><Input dir="ltr" type="number" value={f.salvage_value} onChange={(e) => setF({ ...f, salvage_value: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>العمر الإنتاجي (شهر)</Label><Input dir="ltr" type="number" value={f.useful_life_months} onChange={(e) => setF({ ...f, useful_life_months: e.target.value })} data-testid="fin-asset-life" /></div>
          <AccSel k="expense_account_id" label="حساب مصروف الإهلاك" />
          <AccSel k="accum_account_id" label="حساب مجمع الإهلاك" />
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button onClick={save} disabled={busy} data-testid="fin-save-asset" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
